import { Suspense, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { buildAppRegistry } from '../../../../../../mini-apps/_shared/registry';
import type { MiniAppShellRailConfig } from '../../../../../../mini-apps/_shared/types';
import { NavigationRail } from '../../NavigationRail';
import ErrorBoundary from '../../system/ErrorBoundary';
import { FlowPanels } from './panels/FlowPanels';
import { useFlowPanelsUrlSync } from './panels/useFlowPanelsUrlSync';
import { closePanel, setActionsSuppressed, setFlowClosed } from './panels/flow-panels-store';
import { FlowChatPanelV2 } from './chat/FlowChatPanelV2';
import { useShellFrameStore } from './shell-frame-store';
import ShellHeader from './ShellHeader';
import { resolveChrome, type ShellChrome } from './shell-chrome';
import { GongConnectionBanner } from '../trellus/GongConnectionBanner';
import { TrellusSlot } from '../trellus/TrellusSlot';
import { useMediaQuery } from '../useMediaQuery';
import { OVERLAY_QUERY } from './panels/flow-panels-layout';
import { V2Shell } from './V2Shell';

/**
 * The V2 frame (rail + header + panels) mounted around every authenticated
 * route as a pathless layout route: `RequireAuth > AuthenticatedLayout >
 * ShellLayout > routes`. NEVER place it above `AuthenticatedLayout` — the
 * auth gate must stay outside anything that mounts queries.
 *
 * Header/chat/rail render OUTSIDE `/flow/*`'s `DebugProvider` and
 * `MiniAppLoadErrorBoundary` (nothing under `shell-next/` reads the debug
 * context — checked), so their render errors reach the app-root boundary, not
 * the Flow route's. Accepted.
 *
 * Geometry contract: the content area is `min-h-0 flex-1 overflow-hidden`;
 * pages are expected to use `h-full` roots.
 *
 * The Actions slot is `null` by design. The real Actions content is the
 * enroll-queue dock (`rail/RailDock.tsx`), mounted in `AppRouter.tsx` beside
 * the Outlet; it portals into the slot via `useFlowPanelsActionsNode()`
 * (`flow-panels-slots.ts`), and `useActionsDockSync` keeps the panel store and
 * the dock in lockstep. The Chat slot renders `FlowChatPanelV2` with only
 * `onClose`, wired to `closePanel('chat')` (the panel's close, not the dock's);
 * see `FlowChatPanelV2.tsx`'s header for why two mounts don't duplicate the
 * stream. The Header paints inside the Flow column as its first child, so the
 * top of the screen is one continuous 48px band shared with the Chat and
 * Actions headers. When the Actions column is open in push mode, `RailDock`'s
 * strip hosts `PanelToggles` instead and the search takes their place.
 *
 * Trellus sidebar dock (host layout strategy 2): `[data-flow-content]` is a
 * flex ROW — the page column (`[data-flow-page]`: Gong banner + panels) and the
 * `TrellusSlot kind="sidebar"` as its right sibling. The slot is empty and
 * `shrink-0`, so it has ZERO width while the extension is absent or its sidebar
 * is closed; when open, the injected sidebar app brings its own width (its
 * resize handle) and the flex-1 page column shrinks beside it. No JS width math:
 * `--trellus-clay-sidebar-occupied-width` is never read or subtracted here
 * (subtracting it as well would double-shrink the page).
 *
 * Overlay mode (`OVERLAY_QUERY`, the same line `FlowPanels` collapses its side
 * panels to overlays at): there is NO room to dock, so the slot is deliberately
 * NOT rendered. An absent slot is the intended trigger — the extension then falls
 * back to its own fixed-position sidebar overlaying the page, which is the right
 * behavior on a narrow window. Crossing the line mounts/unmounts the slot; the
 * extension re-scans, which is expected.
 */
function FramedShell({ chrome, pathname }: { chrome: Extract<ShellChrome, { frame: 'v2' }>; pathname: string }) {
  // Always mounted while framed, like the dock's own transport effects — the `?panels=` / `?flowClosed=` deep link has to be consumed on
  // the first commit, before any panel is open to host the hook itself (R11).
  useFlowPanelsUrlSync();

  // Publish the frame signal (read by `useActionsDockSync`, `RailDock`,
  // `useDockUrlSync`, ...). Set at the top of the effect (not only in cleanup)
  // so StrictMode's setup -> cleanup -> setup leaves it true.
  useLayoutEffect(() => {
    useShellFrameStore.setState({ frameActive: true });
    return () => useShellFrameStore.setState({ frameActive: false });
  }, []);

  // Discover (`flowClosed`) belongs to the page the rep is on: any route change
  // — rail click, same-section page, link — turns it off so they see the page
  // they navigated to. A layout effect so the reset lands before the first
  // paint of the new page, and before `useFlowPanelsUrlSync`'s passive READ
  // effect, so a `?flowClosed=true` deep link on initial load still opens
  // Discover. Reset at the top of the effect, no refs: StrictMode-safe.
  useLayoutEffect(() => {
    setFlowClosed(false);
  }, [pathname]);

  const overlay = useMediaQuery(OVERLAY_QUERY);

  return (
    <V2Shell railOverrides={chrome.rail}>
      <div data-flow-content className="flex min-w-0 flex-1 flex-row overflow-hidden bg-bg-primary">
        <div data-flow-page className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <GongConnectionBanner />
          <FlowPanels
            chat={<FlowChatPanelV2 onClose={() => closePanel('chat')} />}
            actions={null}
            header={<ShellHeader variant={chrome.header} />}
          >
            <ErrorBoundary resetKey={pathname} fill="parent">
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </FlowPanels>
        </div>
        {!overlay && <TrellusSlot kind="sidebar" className="h-full shrink-0" />}
      </div>
    </V2Shell>
  );
}

/**
 * Delphi's own chrome, as it was before the V2 frame: the legacy rail (its
 * minimal variant, hover-expand and mobile hamburger) beside the content, with
 * the manifest's rail background painted on the root. Lifted verbatim from
 * `MiniAppLayout`'s old unframed branch. The `pt-12` inset clears the mobile
 * hamburger trigger (`fixed top-3 left-3 z-50`), which reserves no layout space.
 */
function LegacyChrome({ railConfig, children }: { railConfig: MiniAppShellRailConfig | undefined; children: ReactNode }) {
  const background = railConfig?.backgroundColor;
  return (
    <div
      className="flex h-dvh overflow-hidden bg-white dark:bg-nightshade-950"
      style={background ? { backgroundColor: background } : undefined}
    >
      <NavigationRail railConfig={railConfig} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pt-12 min-[768px]:pt-0">{children}</div>
    </div>
  );
}

/**
 * Picks the frame branch from `resolveChrome` (the single source of truth for
 * header variant, rail overrides, and the no-frame opt-out). `frame: 'none'`
 * (Delphi) wraps the Outlet in `LegacyChrome`: no `V2Shell`/`FlowPanels`, no
 * `useFlowPanelsUrlSync` (so the URL is never rewritten with `?panels=`), and
 * `useShellFrameStore.frameActive` stays false. The two branches are different component types, so
 * switching between them unmounts the framed one (frameActive resets via its
 * cleanup) while the panel store, being module-scope, survives.
 */
export default function ShellLayout() {
  const { pathname } = useLocation();
  const manifests = useMemo(() => buildAppRegistry().map((a) => a.manifest), []);
  const chrome = useMemo(() => resolveChrome(pathname, manifests), [pathname, manifests]);

  // The manifest's `shell.hideEnrollQueue` suppresses the Actions column in the
  // panel store (authoritative: `?panels=actions` or a queue producer cannot
  // reopen it). Here, not in `FramedShell`, so Delphi keeps the suppression.
  // Value set at the top of the effect, released in cleanup: StrictMode-safe.
  const actionsHidden = chrome.actionsHidden;
  useLayoutEffect(() => {
    setActionsSuppressed(actionsHidden);
    return () => setActionsSuppressed(false);
  }, [actionsHidden]);

  if (chrome.frame === 'none') {
    return (
      <LegacyChrome railConfig={chrome.legacyRail}>
        <Outlet />
      </LegacyChrome>
    );
  }
  return <FramedShell chrome={chrome} pathname={pathname} />;
}
